export type SimSwapVerificationStatus =
  | 'REQUIRED'
  | 'PENDING'
  | 'PASSED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CONSUMED';

export type SimSwapVerificationFlow = 'EXPRESS_REVERIFY' | 'SUMSUB_ID_LIVENESS';

export type SimSwapVerificationState = {
  initiationAvailable?: boolean;
  initiationUnavailableReason?: string;
  status?: SimSwapVerificationStatus | string | null;
  flow?: SimSwapVerificationFlow | string | null;
  verificationRunUuid?: string | null;
  verifiedAt?: string | null;
  expiresAt?: string | null;
  blockers?: string[];
};

export type SimSwapSummaryState = {
  verificationStatus?: string | null;
  identityEligible: boolean;
  replacementEligible: boolean;
  replacementCheckCurrent: boolean;
  hasPendingSwap: boolean;
};

export const isSimSwapVerificationPassed = (
  verification?: SimSwapVerificationState | null,
) => verification?.status === 'PASSED';

export const isSimSwapVerificationCurrent = (
  verification?: SimSwapVerificationState | null,
  now = Date.now(),
) => {
  if (!isSimSwapVerificationPassed(verification)) return false;
  if (!verification?.verifiedAt || !verification.expiresAt) return false;

  const verifiedAt = Date.parse(verification.verifiedAt);
  const expiresAt = Date.parse(verification.expiresAt);
  if (!Number.isFinite(verifiedAt) || !Number.isFinite(expiresAt)) return false;

  return verifiedAt <= now && now < expiresAt;
};

export const getSimSwapVerificationFlowLabel = (flow?: string | null) => {
  switch (flow) {
    case 'EXPRESS_REVERIFY':
      return 'Sumsub liveness recheck';
    case 'SUMSUB_ID_LIVENESS':
      return 'Sumsub ID + liveness';
    default:
      return 'Identity verification';
  }
};

export const getSimSwapVerificationRequirement = (flow?: string | null) => {
  switch (flow) {
    case 'EXPRESS_REVERIFY':
      return 'A fresh Sumsub liveness check is required because the customer already has a reusable Sumsub identity verification.';
    case 'SUMSUB_ID_LIVENESS':
      return 'A full Sumsub ID and liveness check is required before this SIM swap can be submitted.';
    default:
      return 'The required Sumsub verification path is not available yet. Refresh verification status before continuing.';
  }
};

export const getSimSwapVerificationStatusLabel = (status?: string | null) => {
  switch (status) {
    case 'REQUIRED':
      return 'Required';
    case 'PENDING':
      return 'Pending';
    case 'PASSED':
      return 'Passed';
    case 'FAILED':
      return 'Failed';
    case 'EXPIRED':
      return 'Expired';
    case 'CONSUMED':
      return 'Already used';
    default:
      return 'Unavailable';
  }
};

export const getSimSwapSummaryLabel = ({
  verificationStatus,
  identityEligible,
  replacementEligible,
  replacementCheckCurrent,
  hasPendingSwap,
}: SimSwapSummaryState) => {
  if (hasPendingSwap) return 'Pending';
  if (verificationStatus !== 'PASSED' || !identityEligible) {
    if (verificationStatus === 'PENDING') return 'Verification pending';
    return 'Verification required';
  }
  if (!replacementEligible || !replacementCheckCurrent) {
    return 'Replacement SIM required';
  }
  return 'Ready to submit';
};

export const buildSimSwapEligibilityKey = ({
  accountId,
  accountUuid,
  verificationRunUuid,
  verificationStatus,
  candidateIccid,
  sourceSimIccid,
}: {
  accountId?: number | string | null;
  accountUuid?: string | null;
  verificationRunUuid?: string | null;
  verificationStatus?: string | null;
  candidateIccid?: string | null;
  sourceSimIccid?: string | null;
}) =>
  [
    accountId ?? '',
    accountUuid ?? '',
    verificationRunUuid ?? '',
    verificationStatus ?? '',
    candidateIccid ?? '',
    sourceSimIccid ?? '',
  ].join('|');

export const formatReplacementSimBlocker = (blocker: string) => {
  const message = blocker.trim();
  if (!message) return message;

  const dmaStatus =
    /DMA status must be PENDING_RICA \(currently ([A-Z_]+)\)/.exec(
      message,
    )?.[1];
  if (dmaStatus === 'RECEIVED' || dmaStatus === 'PROCESSING') {
    return `${message} Wait for DMA processing, then click Recheck SIM. If the status does not change, ask operations to investigate.`;
  }
  if (dmaStatus) {
    return `${message} Ask operations to review this DMA record before continuing. Do not RICA the replacement SIM.`;
  }

  const referencesDmaOrMsisdn = /\b(?:dma|msisdn|mobile number)\b/i.test(
    message,
  );
  const indicatesMissingDmaOrMsisdn =
    /\b(?:not|no|missing|pending|awaiting|hasn't|has not|without|unallocated|unassigned)\b/i.test(
      message,
    );
  const alreadyExplainsNextStep = /\b(?:insert|device|recheck|retry)\b/i.test(
    message,
  );

  if (
    referencesDmaOrMsisdn &&
    indicatesMissingDmaOrMsisdn &&
    !alreadyExplainsNextStep
  ) {
    return `${message} Ask the customer to insert this SIM into a device and connect to the network to receive an MSISDN, then click Recheck SIM.`;
  }

  return message;
};
